import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CertificateOrder, CertificateType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const REGISTRAR_ROLES = ['REGISTRAR', 'DEPUTY_REGISTRAR'];

/** Civil Registry — certificate reprint orders (birth/death/marriage), fee-gated issuance via DocVault (M01). */
export function Certificates() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m01/certificates?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<CertificateOrder>>(path);

  return (
    <div>
      <PageHeader
        title="Certificates"
        subtitle="Certificate reprint orders — SeyPay fee intent, then DocVault issuance once paid (M01)."
        actions={
          <div className="flex items-center gap-2">
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="PENDING_PAYMENT">Pending payment</option>
              <option value="PAID">Paid</option>
              <option value="ISSUED">Issued</option>
            </select>
            {isRegistrar ? (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + New order
              </button>
            ) : null}
          </div>
        }
      />

      {!isRegistrar ? <RoleNotice roles={REGISTRAR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No certificate orders match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Type</th>
                <th className="table-head">Record ref</th>
                <th className="table-head">Requested by</th>
                <th className="table-head text-right">Fee</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-medium">{c.type}</td>
                  <td className="table-cell font-mono text-xs">{c.recordRef}</td>
                  <td className="table-cell font-mono text-xs">{c.requestedByNin}</td>
                  <td className="table-cell text-right tabular-nums">{scr(c.feeAmount)}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <CertificateDetailModal
          id={selectedId}
          canAct={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateOrderModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CertificateDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const orderQ = useApiQuery<CertificateOrder>(`/api/v1/m01/certificates/${id}`);
  const order = orderQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const issue = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m01/certificates/${id}/issue`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={order ? `${order.type} certificate — ${order.recordRef}` : 'Certificate order'} onClose={onClose}>
      {orderQ.error ? <ErrorNote message={orderQ.error} /> : null}
      {orderQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {order ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={order.status} />} />
            <Detail label="Fee" value={scr(order.feeAmount)} />
            <Detail label="Requested by" value={order.requestedByNin} mono />
            <Detail label="Fee intent ref" value={order.payIntentRefNo ?? '—'} mono />
            {order.issuedDocSerial ? <Detail label="Issued document serial" value={order.issuedDocSerial} mono /> : null}
          </div>

          {order.status === 'PENDING_PAYMENT' ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-sm text-sky-800">
              Awaiting the SeyPay fee intent to clear (confirmed by webhook) before this can be issued.
            </div>
          ) : null}

          {canAct && order.status === 'PAID' ? (
            <button className="btn-success" disabled={busy} onClick={issue}>
              {busy ? 'Issuing…' : 'Issue via DocVault'}
            </button>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Ordered {formatDateTime(order.createdAt)} · last updated {formatDateTime(order.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateOrderModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState<CertificateType>('BIRTH');
  const [recordRef, setRecordRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m01/certificates', { type, recordRef });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New certificate reprint order" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Certificate type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as CertificateType)}>
            <option value="BIRTH">Birth</option>
            <option value="DEATH">Death</option>
            <option value="MARRIAGE">Marriage</option>
          </select>
        </Field>
        <Field label="Record reference (the REGISTERED birth/death/marriage record id)">
          <input className="field-input font-mono" value={recordRef} onChange={(e) => setRecordRef(e.target.value)} placeholder="e.g. clx1a2b3c4d5e6f" />
        </Field>
        <p className="text-xs text-slate-400">
          Creates a SCR 50 SeyPay fee intent. The order can be issued once the fee shows PAID.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || recordRef.trim().length < 3}>
            {busy ? 'Creating…' : 'Create order'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
